import React from 'react';
import { connect } from '@reverbdotcom/commons/src/connect';
import * as elog from '@reverbdotcom/commons/src/elog';

import classNames from 'classnames';
import { core_apimessages_HomepageNotification } from '@reverbdotcom/commons/src/rql';

import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import experiments from '@reverbdotcom/commons/src/experiments';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCEyebrow, RCCloseButton } from '@reverbdotcom/cadence/components';
import { withDismissUserUpdateMutation, DismissUserUpdateMutationFn, useDismissUserUpdate } from './dismiss_homepage_notification_mutation';
import { motion } from 'framer-motion';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

interface ActivityCardProps {
  notification: core_apimessages_HomepageNotification;
}

interface MutationProps {
  dismissUserUpdate: DismissUserUpdateMutationFn;
}

export type Props = ActivityCardProps & MutationProps;

const CARD_COMPONENT_NAME = 'HomepageNotificationActivityCard';
const PRICE_DROP_TYPES = ['PRICE_DROP_ON_WATCHED', 'PRICE_DROP_ON_ITEM_IN_CART'];
const OFFER_ACCEPTED_TYPE = 'OFFER_ACCEPTED';
const NEW_OFFER_TYPE = 'NEW_OFFER';
const OFFER_COUNTERED_TYPE = 'OFFER_COUNTERED';

export function ActivityCardPlaceholder() {
  return (
    <div className={classNames('homepage-notifications', 'homepage-notifications__placeholder')}>
      <div className="homepage-notifications__thumbnail" />
      <div className="homepage-notifications__content">
        <div className="homepage-notifications__eyebrow" />
        <div className="homepage-notifications__description" />
      </div>
    </div>
  );
}

function InternalHomepageNotificationActivityCard(props: Props) {
  const { notification } = props;
  const user = useUser();
  const { dismissUserUpdate } = useDismissUserUpdate(props.dismissUserUpdate, notification.updateId);

  const handleDismiss = async function () {
    try {
      await dismissUserUpdate();
    } catch (e) {
      elog.error(
        e.message,
        { componentName: CARD_COMPONENT_NAME },
        e,
      );
    }
  };

  const handleOnClick = () => {
    handleDismiss();
    trackEvent({
      componentName: CARD_COMPONENT_NAME,
      eventName: MParticleEventName.ClickedHomepageNotification,
      experiments: formatExperimentsForEventAttributes(user, [experiments.HOMEPAGE_NOTIFICATIONS_ROW]),
      entityType: notification.type,
    });
  };

  const handleClose = () => {
    handleDismiss();
    trackEvent({
      componentName: CARD_COMPONENT_NAME,
      eventName: MParticleEventName.DismissedHomepageNotification,
      experiments: formatExperimentsForEventAttributes(user, [experiments.HOMEPAGE_NOTIFICATIONS_ROW]),
      entityType: notification.type,
    });
  };

  const eyebrowClass = (notificationType) => {
    if (PRICE_DROP_TYPES.includes(notificationType)) {
      return 'homepage-notifications__eyebrow-alert';
    }
    if (notificationType === OFFER_COUNTERED_TYPE || notificationType === NEW_OFFER_TYPE) {
      return 'homepage-notifications__eyebrow-callout';
    }
    if (notificationType === OFFER_ACCEPTED_TYPE) {
      return 'homepage-notifications__eyebrow-success';
    }

    return '';
  };

  const displayFollowText = (notificationType) => {
    return PRICE_DROP_TYPES.includes(notificationType);
  };

  return (
    <motion.div
      className="homepage-notifications"
      key={notification.id}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
        transition: {
          duration: 0.1,
        },
      }}
      transition={{
        duration: 0.6,
        ease: 'easeOut',
      }}
      layout
    >
      <CoreLink
        to={notification.ctaUrl}
        className="homepage-notifications__thumbnail"
        onClick={() => handleOnClick()}>
        <img
          className="homepage-notifications__thumbnail-image"
          src={notification.imageUrl}
          alt={notification.descriptionText} />
      </CoreLink>
      <div className="homepage-notifications__content">
        <div className="homepage-notifications__content-main">
          <CoreLink to={notification.ctaUrl} onClick={() => handleOnClick()}>
            <div className={`homepage-notifications__eyebrow ${eyebrowClass(notification.type)}`}>
              <RCEyebrow text={notification.typeText} truncate />
            </div>
            <div className="homepage-notifications__description">
              {notification.descriptionText}
            </div>
          </CoreLink>
          <div className="homepage-notifications__close-button">
            <RCCloseButton onClick={() => handleClose()} useAltIcon />
          </div>
        </div>
        <CoreLink to={notification.ctaUrl} onClick={() => handleOnClick()}>
          <div className="homepage-notifications__callout">
            <div className={`${ displayFollowText(notification.type) ? 'homepage-notifications__callout-price-overflow' : ''}`}>
              <span className="homepage-notifications__callout__primary-text">{notification.calloutText}</span>
              { displayFollowText(notification.type) &&
                <span className="homepage-notifications__callout__secondary-text">{notification.followText}</span>}
            </div>
            { !displayFollowText(notification.type) &&
              <span className="homepage-notifications__deadline-text">{notification.deadlineText}</span>
            }
          </div>
        </CoreLink>
      </div>
    </motion.div>
  );
}

export const HomepageNotificationActivityCard = connect<ActivityCardProps>([
  withDismissUserUpdateMutation,
])(InternalHomepageNotificationActivityCard);
